var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createElement as _createElement } from "react";
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { Profiler, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { scaleLinear } from 'd3';
import { DragDropContext, Draggable, Droppable, } from 'react-beautiful-dnd';
import { Button, Card, ConditionalWrapper, KeyDownProvider, useParcaContext, useURLState, } from '@parca/components';
import { useContainerDimensions } from '@parca/dynamicsize';
import { getNewSpanColor } from '@parca/functions';
import { selectDarkMode, useAppSelector } from '@parca/store';
import { Callgraph } from '../';
import ProfileIcicleGraph from '../ProfileIcicleGraph';
import { TopTable } from '../TopTable';
import ProfileShareButton from '../components/ProfileShareButton';
import useDelayedLoader from '../useDelayedLoader';
import FilterByFunctionButton from './FilterByFunctionButton';
import ViewSelector from './ViewSelector';
import { VisualizationPanel } from './VisualizationPanel';
function arrayEquals(a, b) {
    return (Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every(function (val, index) { return val === b[index]; }));
}
export var ProfileView = function (_a) {
    var flamegraphData = _a.flamegraphData, topTableData = _a.topTableData, callgraphData = _a.callgraphData, sampleUnit = _a.sampleUnit, profileSource = _a.profileSource, queryClient = _a.queryClient, navigateTo = _a.navigateTo, onDownloadPProf = _a.onDownloadPProf, onFlamegraphContainerResize = _a.onFlamegraphContainerResize;
    var _b = useContainerDimensions(), ref = _b.ref, dimensions = _b.dimensions;
    var _c = useState([]), curPath = _c[0], setCurPath = _c[1];
    var _d = useURLState({
        param: 'dashboard_items',
        navigateTo: navigateTo,
    }), rawDashboardItems = _d[0], setDashboardItems = _d[1];
    var currentSearchString = useURLState({ param: 'search_string' })[0];
    var dashboardItems = rawDashboardItems;
    var isDarkMode = useAppSelector(selectDarkMode);
    var isMultiPanelView = dashboardItems.length > 1;
    var _e = useParcaContext(), loader = _e.loader, perf = _e.perf;
    useEffect(function () {
        // Reset the current path when the profile source changes
        setCurPath([]);
    }, [profileSource]);
    var isLoading = useMemo(function () {
        if (dashboardItems.includes('icicle')) {
            return Boolean(flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.loading);
        }
        if (dashboardItems.includes('callgraph')) {
            return Boolean(callgraphData === null || callgraphData === void 0 ? void 0 : callgraphData.loading);
        }
        if (dashboardItems.includes('table')) {
            return Boolean(topTableData === null || topTableData === void 0 ? void 0 : topTableData.loading);
        }
        return false;
    }, [dashboardItems, callgraphData === null || callgraphData === void 0 ? void 0 : callgraphData.loading, flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.loading, topTableData === null || topTableData === void 0 ? void 0 : topTableData.loading]);
    var isLoaderVisible = useDelayedLoader(isLoading);
    if ((flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.error) != null) {
        console.error('Error: ', flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.error);
        return (_jsxs("div", __assign({ className: "p-10 flex justify-center" }, { children: ["An error occurred: ", flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.error.message] })));
    }
    var setNewCurPath = function (path) {
        if (!arrayEquals(curPath, path)) {
            setCurPath(path);
        }
    };
    var maxColor = getNewSpanColor(isDarkMode);
    var minColor = scaleLinear([isDarkMode ? 'black' : 'white', maxColor])(0.3);
    var colorRange = [minColor, maxColor];
    var getDashboardItemByType = function (_a) {
        var type = _a.type, isHalfScreen = _a.isHalfScreen, setActionButtons = _a.setActionButtons;
        switch (type) {
            case 'icicle': {
                return (flamegraphData === null || flamegraphData === void 0 ? void 0 : flamegraphData.data) != null ? (_jsx(ConditionalWrapper, __assign({ condition: (perf === null || perf === void 0 ? void 0 : perf.onRender) != null, WrapperComponent: Profiler, wrapperProps: {
                        id: 'icicleGraph',
                        onRender: perf === null || perf === void 0 ? void 0 : perf.onRender,
                    } }, { children: _jsx(ProfileIcicleGraph, { curPath: curPath, setNewCurPath: setNewCurPath, graph: flamegraphData.data, sampleUnit: sampleUnit, onContainerResize: onFlamegraphContainerResize, navigateTo: navigateTo, loading: flamegraphData.loading, setActionButtons: setActionButtons }) }))) : (_jsx(_Fragment, { children: " " }));
            }
            case 'callgraph': {
                return (callgraphData === null || callgraphData === void 0 ? void 0 : callgraphData.data) != null && (dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) !== undefined ? (_jsx(Callgraph, { graph: callgraphData.data, sampleUnit: sampleUnit, width: isHalfScreen ? (dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) / 2 : dimensions === null || dimensions === void 0 ? void 0 : dimensions.width, colorRange: colorRange })) : (_jsx(_Fragment, {}));
            }
            case 'table': {
                return topTableData != null ? (_jsx(TopTable, { loading: topTableData.loading, data: topTableData.data, sampleUnit: sampleUnit, navigateTo: navigateTo, setActionButtons: setActionButtons, currentSearchString: currentSearchString })) : (_jsx(_Fragment, {}));
            }
            default: {
                return _jsx(_Fragment, {});
            }
        }
    };
    var handleClosePanel = function (visualizationType) {
        var newDashboardItems = dashboardItems.filter(function (item) { return item !== visualizationType; });
        setDashboardItems(newDashboardItems);
    };
    var onDragEnd = function (result) {
        var destination = result.destination, source = result.source, draggableId = result.draggableId;
        if (Boolean(destination) && (destination === null || destination === void 0 ? void 0 : destination.index) !== source.index) {
            var targetItem_1 = draggableId;
            var otherItems = dashboardItems.filter(function (item) { return item !== targetItem_1; });
            var newDashboardItems = destination.index < source.index
                ? __spreadArray([targetItem_1], otherItems, true) : __spreadArray(__spreadArray([], otherItems, true), [targetItem_1], false);
            setDashboardItems(newDashboardItems);
        }
    };
    return (_jsx(KeyDownProvider, { children: _jsx("div", __assign({ className: "py-3" }, { children: _jsx(Card, { children: _jsxs(Card.Body, { children: [_jsxs("div", __assign({ className: "flex py-3 w-full" }, { children: [_jsxs("div", __assign({ className: "lg:w-1/2 flex space-x-4" }, { children: [_jsxs("div", __assign({ className: "flex space-x-1" }, { children: [profileSource != null && queryClient != null ? (_jsx(ProfileShareButton, { queryRequest: profileSource.QueryRequest(), queryClient: queryClient })) : null, _jsx(Button, __assign({ className: "!w-auto", color: "neutral", onClick: function (e) {
                                                        e.preventDefault();
                                                        onDownloadPProf();
                                                    } }, { children: "Download pprof" }))] })), _jsx(FilterByFunctionButton, { navigateTo: navigateTo })] })), _jsx("div", __assign({ className: "flex ml-auto gap-2" }, { children: _jsx(ViewSelector, { defaultValue: "", navigateTo: navigateTo, position: -1, placeholderText: "Add panel...", primary: true, addView: true, disabled: isMultiPanelView || dashboardItems.length < 1 }) }))] })), isLoaderVisible ? (_jsx(_Fragment, { children: loader })) : (_jsx(DragDropContext, __assign({ onDragEnd: onDragEnd }, { children: _jsx("div", __assign({ className: "w-full", ref: ref }, { children: _jsx(Droppable, __assign({ droppableId: "droppable", direction: "horizontal" }, { children: function (provided) { return (_jsx("div", __assign({ ref: provided.innerRef, className: "flex space-x-4 justify-between w-full" }, provided.droppableProps, { children: dashboardItems.map(function (dashboardItem, index) {
                                            return (_jsx(Draggable, __assign({ draggableId: dashboardItem, index: index, isDragDisabled: !isMultiPanelView }, { children: function (provided, snapshot) { return (_createElement("div", __assign({ ref: provided.innerRef }, provided.draggableProps, { key: dashboardItem, className: cx('border dark:bg-gray-700 rounded border-gray-300 dark:border-gray-500 p-3', isMultiPanelView ? 'w-1/2' : 'w-full', snapshot.isDragging ? 'bg-gray-200' : 'bg-white') }),
                                                    _jsx(VisualizationPanel, { handleClosePanel: handleClosePanel, isMultiPanelView: isMultiPanelView, dashboardItem: dashboardItem, getDashboardItemByType: getDashboardItemByType, dragHandleProps: provided.dragHandleProps, navigateTo: navigateTo, index: index }))); } }), dashboardItem));
                                        }) }))); } })) })) })))] }) }) })) }));
};
